import { useState, useRef, useEffect } from 'react';
import {
  X, Send, MessageSquare, Clock, User, Loader2,
  CheckCircle2, AlertCircle, Shield, Plus, Paperclip, FileText,
  ArrowLeft, Download, Eye,
} from 'lucide-react';
import { PageShell } from './layout/PageShell';
import type {
  ReviewHistoryEntry,
  SupportingEvidenceResponse,
  AssessmentExecutionViewIteration,
} from '../../api/frameworksApi';
import { getSupportingEvidence, downloadSupportingEvidenceFile, previewSupportingEvidenceFile } from '../../api/frameworksApi';
import { FilePreviewBody, FilePreviewHeader } from './FilePreview';
import { useToastStore } from "../../stores/toastStore";

/* ── Types ── */

export interface ChatMessage {
  id: string;
  side: 'assessor' | 'system-owner';
  type: 'question' | 'response' | 'status';
  text: string;
  by: string;
  at: string;
  status?: string;
  evidenceIds?: string[];
}

interface FollowUpChatModalProps {
  open: boolean;
  onClose: () => void;
  statementId: string;
  controlId: string;
  reviewHistory: ReviewHistoryEntry[];
  /** Full execution-view iteration timeline (oldest → newest). When supplied
   *  and non-empty, takes precedence over `reviewHistory` for message
   *  construction — used on pages that no longer call the review API. */
  iterations?: AssessmentExecutionViewIteration[];
  currentFollowUpQuestion?: string | null;
  /** If provided, shows a reply input at the bottom (system owner mode) */
  onSendResponse?: (text: string) => Promise<void>;
  /** If provided, allows the assessor to send a new follow-up question */
  onSendFollowUp?: (text: string) => Promise<void>;
  /** Current review status */
  currentStatus: string;
  /**
   * Controls message alignment:
   * - "execution": System Owner on right, Assessor on left
   * - "review": Assessor on right, System Owner on left (default)
   */
  perspective?: 'execution' | 'review';
  /** Callback for attaching files (WhatsApp-style "+" button) */
  onAttachFile?: () => void;
  /** Whether evidence is required for this follow-up */
  needsEvidence?: boolean;
  /** Callback to open evidence picker */
  onAttachEvidence?: () => void;
  /** Number of evidence files attached */
  attachedEvidenceCount?: number;
  /** Callback to go back to the follow-up response modal */
  onBack?: () => void;
}

/* ── Helpers ── */

function shortId(uuid: string): string {
  return uuid.slice(0, 8);
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB') +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function statusLabel(status: string): { text: string; color: string } {
  switch (status) {
    case 'accepted': return { text: 'Accepted', color: 'emerald' };
    case 'follow_up_raised': return { text: 'Follow-Up Raised', color: 'amber' };
    case 'follow_up_resolved': return { text: 'Response Submitted', color: 'sky' };
    case 'pending': return { text: 'Pending Review', color: 'slate' };
    default: return { text: status, color: 'slate' };
  }
}

/* ── Component ── */

export function FollowUpChatModal({
  open,
  onClose,
  statementId,
  controlId,
  reviewHistory,
  iterations,
  currentFollowUpQuestion,
  onSendResponse,
  onSendFollowUp,
  currentStatus,
  perspective = 'review',
  onAttachFile,
  needsEvidence,
  onAttachEvidence,
  attachedEvidenceCount = 0,
  onBack,
}: FollowUpChatModalProps) {
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [evidenceDetails, setEvidenceDetails] = useState<Record<string, SupportingEvidenceResponse>>({});
  /* Evidence preview - the shared engine (components/FilePreview) fetches the
     file, sniffs its real type and renders PDFs, images, text and Word
     documents inline, so opening the modal is all that's needed here. */
  const [previewEvidenceId, setPreviewEvidenceId] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const handleEvidencePreview = (evidenceId: string, title: string) => {
    setPreviewTitle(title);
    setPreviewEvidenceId(evidenceId);
  };

  const closeEvidencePreview = () => {
    setPreviewEvidenceId(null);
    setPreviewTitle('');
  };

  const handleEvidenceDownload = async (evidenceId: string, fileName?: string) => {
    try {
      const url = downloadSupportingEvidenceFile(evidenceId);
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('File not available');
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/xml') || contentType.includes('text/xml')) {
        throw new Error('File not available');
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName || `evidence-${evidenceId}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
      useToastStore.getState().showError("Error", 'File is not available for this evidence.');
    }
  };

  // Fetch evidence details for all follow-up evidence IDs in review history
  useEffect(() => {
    const allIds = reviewHistory
      .flatMap(e => e.followUpEvidenceIds ?? [])
      .filter(id => !evidenceDetails[id]);
    if (allIds.length === 0) return;
    const uniqueIds = [...new Set(allIds)];
    Promise.all(
      uniqueIds.map(id =>
        getSupportingEvidence(id)
          .then(res => ({ id, data: res.data }))
          .catch(() => null),
      ),
    ).then(results => {
      const newDetails: Record<string, SupportingEvidenceResponse> = {};
      for (const r of results) {
        if (r?.data) newDetails[r.id] = r.data;
      }
      if (Object.keys(newDetails).length > 0) {
        setEvidenceDetails(prev => ({ ...prev, ...newDetails }));
      }
    });
  }, [reviewHistory]);

  // Build chat messages. iterations[] (execution-view) takes precedence when
  // provided; otherwise fall back to the legacy reviewHistory path.
  const messages: ChatMessage[] = [];
  if (iterations && iterations.length > 0) {
    for (const iter of iterations) {
      // Executor's save in this round
      if (iter.justification) {
        messages.push({
          id: `iter-${iter.iteration_number}-save`,
          side: 'system-owner',
          type: 'response',
          text: iter.justification,
          by: 'System Owner',
          at: iter.saved_at ?? '',
          status: 'follow_up_resolved',
        });
      }
      // Reviewer's terminal verdict in this round
      const rs = (iter.review_status ?? '').toLowerCase();
      if (rs === 'accepted' || rs === 'approved') {
        messages.push({
          id: `iter-${iter.iteration_number}-status`,
          side: 'assessor',
          type: 'status',
          text: 'Accepted',
          by: 'Assessor',
          at: iter.reviewed_at ?? '',
          status: 'accepted',
        });
      } else if (rs === 'rejected') {
        messages.push({
          id: `iter-${iter.iteration_number}-status`,
          side: 'assessor',
          type: 'status',
          text: 'Rejected',
          by: 'Assessor',
          at: iter.reviewed_at ?? '',
          status: 'rejected',
        });
      }
      // Reviewer's follow-up question in this round
      if (iter.reviewer_note) {
        messages.push({
          id: `iter-${iter.iteration_number}-q`,
          side: 'assessor',
          type: 'question',
          text: iter.reviewer_note,
          by: 'Assessor',
          at: iter.reviewed_at ?? iter.saved_at ?? '',
          status: 'follow_up_raised',
        });
      }
    }
  } else {
    for (const entry of reviewHistory) {
      // Status change message
      if (entry.reviewStatus === 'accepted') {
        messages.push({
          id: `${entry.id}-status`,
          side: 'assessor',
          type: 'status',
          text: 'Accepted',
          by: entry.reviewedBy,
          at: entry.reviewedAt,
          status: 'accepted',
        });
      }

      // Assessor's follow-up question
      if (entry.followUpQuestion) {
        messages.push({
          id: `${entry.id}-q`,
          side: 'assessor',
          type: 'question',
          text: entry.followUpQuestion,
          by: entry.reviewedBy,
          at: entry.reviewedAt,
          status: 'follow_up_raised',
        });
      }

      // System owner's response — skip sentinel decision values stored by the
      // backend in followUpResponse (e.g. "rejected", "accepted") which are
      // assessor verdicts, not actual SO replies.
      const decisionSentinels = new Set(["rejected", "accepted", "approved"]);
      if (
        entry.followUpResponse &&
        !decisionSentinels.has(entry.followUpResponse.trim().toLowerCase())
      ) {
        messages.push({
          id: `${entry.id}-r`,
          side: 'system-owner',
          type: 'response',
          text: entry.followUpResponse,
          by: entry.followUpRespondedBy ?? entry.reviewedBy,
          at: entry.followUpRespondedAt ?? entry.reviewedAt,
          status: 'follow_up_resolved',
          evidenceIds: entry.followUpEvidenceIds,
        });
      }
    }
  }

  // Sort messages chronologically so responses appear in correct order
  messages.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  // If there's a current follow-up question not in history yet
  if (
    currentFollowUpQuestion &&
    currentStatus === 'follow_up_raised' &&
    !messages.some(m => m.text === currentFollowUpQuestion && m.type === 'question')
  ) {
    messages.push({
      id: 'current-followup',
      side: 'assessor',
      type: 'question',
      text: currentFollowUpQuestion,
      by: 'Assessor',
      at: new Date().toISOString(),
      status: 'follow_up_raised',
    });
  }

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, open]);

  const MIN_WORDS = 20;
  const inputWordCount = inputText.trim() ? inputText.trim().split(/\s+/).length : 0;
  const meetsMinWords = inputWordCount >= MIN_WORDS;

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || sending || !meetsMinWords) return;
    setSending(true);
    try {
      if (onSendResponse) {
        await onSendResponse(text);
      } else if (onSendFollowUp) {
        await onSendFollowUp(text);
      }
      setInputText('');
      setAttachedFiles([]);
      onClose();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) return null;

  const canReply = !!(onSendResponse || onSendFollowUp);
  const placeholder = onSendResponse
    ? 'Type your response'
    : onSendFollowUp
      ? 'Type your follow-up question'
      : 'Type a message';

  const stLabel = statusLabel(currentStatus);

  return (
    <PageShell className="fixed inset-0 z-[60] flex justify-center overflow-y-auto p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-3xl my-auto bg-white dark:bg-[#1a1a2e] rounded-2xl shadow-2xl border border-[#e2e8f0] dark:border-white/[0.08] flex flex-col max-h-[760px] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-gradient-to-r from-indigo-50 to-white dark:from-indigo-500/[0.06] dark:to-transparent">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="size-8 rounded-lg flex items-center justify-center text-[#94a3b8] dark:text-white/40 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] hover:text-[#475569] dark:hover:text-white/60 transition-colors"
                title="Back to Follow-Up"
              >
                <ArrowLeft className="size-4" />
              </button>
            )}
            <div className="size-9 rounded-xl bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center">
              <MessageSquare className="size-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-[13px] font-bold text-[#0f172a] dark:text-white leading-tight">Follow-Up Conversation</p>
              <p className="text-[10px] text-[#94a3b8] dark:text-white/30 mt-0.5">
                {controlId?.toUpperCase()} &middot; {statementId?.toUpperCase()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-${stLabel.color}-50 dark:bg-${stLabel.color}-500/10 text-${stLabel.color}-700 dark:text-${stLabel.color}-400`}>
              <span className={`size-1.5 rounded-full bg-${stLabel.color}-500`} />
              {stLabel.text}
            </span>
            <button
              onClick={onClose}
              className="size-8 rounded-lg flex items-center justify-center text-[#94a3b8] dark:text-white/30 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] hover:text-[#475569] dark:hover:text-white/60 transition-colors"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* Chat area */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-4 min-h-[300px] max-h-[60vh]">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="size-12 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center">
                <MessageSquare className="size-5 text-[#cbd5e1] dark:text-white/15" />
              </div>
              <p className="text-[13px] text-[#94a3b8] dark:text-white/30">No conversation history yet</p>
            </div>
          ) : (
            messages.map(msg => {
              // Status message (center)
              if (msg.type === 'status') {
                return (
                  <div key={msg.id} className="flex justify-center">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/10">
                      <CheckCircle2 className="size-3 text-emerald-500" />
                      <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400">Statement Accepted</span>
                      <span className="text-[9px] text-emerald-500/60 dark:text-emerald-400/40 ml-1">{formatTime(msg.at)}</span>
                    </div>
                  </div>
                );
              }

              const isAssessor = msg.side === 'assessor';
              // In execution perspective: system-owner on right, assessor on left
              // In review perspective (default): assessor on right, system-owner on left
              const isRight = perspective === 'execution' ? !isAssessor : isAssessor;
              return (
                <div key={msg.id} className={`flex ${isRight ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[80%]">
                    {/* Sender label */}
                    <div className={`flex items-center gap-1.5 mb-1 ${isRight ? 'justify-end' : 'justify-start'}`}>
                      <div className={`size-4 rounded-full flex items-center justify-center ${
                        isAssessor
                          ? 'bg-indigo-100 dark:bg-indigo-500/15'
                          : 'bg-emerald-100 dark:bg-emerald-500/15'
                      }`}>
                        {isAssessor
                          ? <Shield className="size-2.5 text-indigo-600 dark:text-indigo-400" />
                          : <User className="size-2.5 text-emerald-600 dark:text-emerald-400" />
                        }
                      </div>
                      <span className={`text-[9px] font-bold tracking-wider ${
                        isAssessor
                          ? 'text-indigo-500 dark:text-indigo-400'
                          : 'text-emerald-500 dark:text-emerald-400'
                      }`}>
                        {isAssessor ? 'Assessor' : 'System Owner'}
                      </span>
                    </div>

                    {/* Message bubble */}
                    <div className={`px-3.5 py-2.5 rounded-2xl ${
                      isRight
                        ? 'bg-indigo-600 dark:bg-violet-600 text-white rounded-tr-sm'
                        : 'bg-[#f1f5f9] dark:bg-white/[0.06] text-[#1e293b] dark:text-white/80 rounded-tl-sm'
                    }`}>
                      <p className="text-[12px] leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                    </div>

                    {/* Follow-up evidence attachments */}
                    {msg.evidenceIds && msg.evidenceIds.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {msg.evidenceIds.map(evId => {
                          const ev = evidenceDetails[evId];
                          const label = ev?.title || ev?.fileName || `Evidence ${evId.slice(0, 8)}`;
                          return (
                            <div
                              key={evId}
                              className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <FileText className="size-3 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                                <span className="text-[10px] font-medium text-amber-800 dark:text-amber-300 truncate">
                                  {label}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={() => handleEvidencePreview(evId, label)}
                                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors"
                                >
                                  <Eye className="size-2.5" /> Preview
                                </button>
                                <button
                                  onClick={() => handleEvidenceDownload(evId, label)}
                                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors"
                                >
                                  <Download className="size-2.5" /> Download
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className={`flex items-center gap-1 mt-1 ${isRight ? 'justify-end' : 'justify-start'}`}>
                      <Clock className="size-2.5 text-[#c0c9d4] dark:text-white/15" />
                      <span className="text-[9px] text-[#94a3b8] dark:text-white/20">{formatTime(msg.at)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Input area */}
        {canReply && (
          <div className="px-4 py-3 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02]">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length > 0) {
                  setAttachedFiles(prev => [...prev, ...files]);
                }
                e.target.value = '';
              }}
            />

            {/* Attached files preview */}
            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {attachedFiles.map((file, idx) => (
                  <div
                    key={`${file.name}-${idx}`}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-indigo-50 dark:bg-violet-500/10 border border-indigo-200 dark:border-violet-500/20 max-w-[180px]"
                  >
                    <FileText className="size-3 text-indigo-500 dark:text-violet-400 flex-shrink-0" />
                    <span className="text-[10px] font-medium text-indigo-700 dark:text-violet-300 truncate">
                      {file.name}
                    </span>
                    <button
                      onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== idx))}
                      className="flex-shrink-0 p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-violet-500/20 transition-colors"
                    >
                      <X className="size-2.5 text-indigo-400 dark:text-violet-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input bar */}
            <div className="flex items-end gap-2">
              <div className="flex-1 rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] focus-within:ring-2 focus-within:ring-indigo-500/20 dark:focus-within:ring-violet-500/20 focus-within:border-indigo-500 dark:focus-within:border-violet-500/50 transition-all overflow-hidden">
                <textarea
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  rows={1}
                  className="w-full text-[12px] px-3.5 py-2.5 outline-none bg-transparent placeholder:text-[#c0c9d4] dark:placeholder:text-white/20 resize-none text-[#0f172a] dark:text-white min-h-[36px] max-h-[100px] border-none focus:ring-0"
                  style={{ height: 'auto' }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = Math.min(target.scrollHeight, 100) + 'px';
                  }}
                />
                {needsEvidence && onAttachEvidence && (
                  <>
                    <div className="border-t border-[#e2e8f0] dark:border-white/[0.07]" />
                    <div className="flex items-center gap-2 px-3 py-1.5">
                      <button
                        onClick={onAttachEvidence}
                        className="p-1 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
                        title="Attach Evidence"
                      >
                        <Paperclip className="size-4 text-[#64748b] dark:text-white/40 hover:text-amber-500 dark:hover:text-amber-400 transition-colors" />
                      </button>
                      {attachedEvidenceCount > 0 ? (
                        <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                          {attachedEvidenceCount} file(s) attached
                        </span>
                      ) : (
                        <span className="text-[11px] text-red-400 font-medium">
                          Evidence required
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={handleSend}
                disabled={!inputText.trim() || sending || !meetsMinWords}
                className="size-9 rounded-xl bg-indigo-600 dark:bg-violet-600 text-white flex items-center justify-center hover:bg-indigo-700 dark:hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex-shrink-0 shadow-sm shadow-indigo-500/20 dark:shadow-violet-500/20"
              >
                {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              </button>
            </div>
            <div className="flex justify-end mt-1">
              <span className={`text-[11px] font-medium ${meetsMinWords ? 'text-emerald-500 dark:text-emerald-400' : 'text-amber-500 dark:text-amber-400'}`}>
                {inputWordCount}/{MIN_WORDS} words {meetsMinWords ? '' : '(minimum required)'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Evidence preview modal */}
      {previewEvidenceId && (
        <div className="fixed inset-0 z-[60] flex justify-center overflow-y-auto p-4">
          <div className="fixed inset-0 bg-black/50" onClick={closeEvidencePreview} />
          <div className="relative w-full max-w-6xl my-auto h-[860px] max-h-[860px] bg-white dark:bg-[#1a1a2e] rounded-2xl shadow-2xl border border-[#e2e8f0] dark:border-white/[0.08] overflow-hidden flex flex-col">
            <FilePreviewHeader
              title={previewTitle}
              fileName={null}
              downloadUrl={downloadSupportingEvidenceFile(previewEvidenceId)}
              onClose={closeEvidencePreview}
            />
            <FilePreviewBody
              url={previewSupportingEvidenceFile(previewEvidenceId)}
              fileName={null}
            />
          </div>
        </div>
      )}
    </PageShell>
  );
}
